import React, { useState } from 'react'
import Big from 'big.js'
import { useFormContext } from 'react-hook-form'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Stack,
  Box,
} from '@mui/material'
import { ROUNDING_PRECISIONS } from 'utils/constants'
import type { InvoicePaymentCreation } from 'components/containers/invoice-payments/types'
import { safeNumber } from 'components/containers/invoice-payments/utils/utils'

interface QuickAddAdjustmentsProps {
  open: boolean
  onClose: () => void
  onAddAdjustments: (invoiceAdjustments: { id: string; adjustment: number }[], adjustmentNote: string) => void
  selectedInvoices: InvoicePaymentCreation[]
}

const QuickAddAdjustments: React.FC<QuickAddAdjustmentsProps> = ({
  open,
  onClose,
  onAddAdjustments,
  selectedInvoices,
}) => {
  const { getValues } = useFormContext()
  const [adjustmentNote, setAdjustmentNote] = useState('')

  const handleAddAdjustments = () => {
    const invoiceAdjustments = selectedInvoices.map(invoice => {
      const invoiceFormData = getValues(`invoices.${invoice.id}`)
      const amountDue = Big(safeNumber(invoice.amountDue))
      const amountPaid = Big(safeNumber(invoiceFormData.amountPaid))
      const adjustment = amountDue.sub(amountPaid).round(ROUNDING_PRECISIONS.COST).toNumber()

      return {
        id: invoice.id,
        adjustment,
      }
    })

    onAddAdjustments(invoiceAdjustments, adjustmentNote)
    onClose()
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth data-testid="quick-add-adjustments-dialog">
      <DialogTitle>
        <Typography variant="h6" data-testid="dialog-title">
          Quick Add Adjustments
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box component="form" data-testid="adjustments-form">
          <Typography data-testid="adjustments-description">
            All selected invoices with a positive or negative balance will receive a balance adjustment that zeroes out
            the balance.
          </Typography>
          <TextField
            fullWidth
            margin="normal"
            variant="outlined"
            placeholder="Adjustment Note"
            helperText="Optional"
            value={adjustmentNote}
            onChange={e => setAdjustmentNote(e.target.value)}
            data-testid="adjustment-note-input"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button variant="text" onClick={onClose} aria-label="Cancel" data-testid="cancel-button">
            CANCEL
          </Button>
          <Button
            variant="text"
            onClick={handleAddAdjustments}
            aria-label="Add Adjustments"
            data-testid="add-adjustments-button"
          >
            ADD ADJUSTMENTS
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  )
}

export default QuickAddAdjustments
